import React, { useState, useEffect, useRef } from 'react';
import InvoiceLetterhead from '../components/InvoiceLetterhead';
import { ReceiptLetterhead, QuotationLetterhead, CreditNoteLetterhead } from '../components/DocumentLetterheads';
import { useToast } from './Settings';
import { buildFirmAddress, addressOverlap } from '../utils/firmAddress';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function BillingSettings({ token, darkMode = false, T: theme }) {
  const { showToast, showConfirm } = useToast();
  // Dark mode token fallback — works standalone or when T passed from parent
  const T = theme || (darkMode ? {
    bg: '#0f172a', bg2: '#1e293b', bg3: '#334155',
    border: '#334155', border2: '#475569',
    text: '#f1f5f9', text2: '#94a3b8', text3: '#64748b',
    cardBg: '#1e293b', cardBorder: '#334155',
    inputBg: '#1e293b', inputBorder: '#475569',
  } : {
    bg: '#f1f5f9', bg2: '#fff', bg3: '#f8faff',
    border: '#e8edf5', border2: '#e2e8f0',
    text: '#0f1f45', text2: '#4a5568', text3: '#94a3b8',
    cardBg: '#fff', cardBorder: '#f1f5f9',
    inputBg: '#fff', inputBorder: '#e2e8f0',
  });
  const [activeSection, setActiveSection] = useState('company');
  const [previewType, setPreviewType] = useState('invoice');

  const handleSectionChange = (key) => {
    setActiveSection(key);
    if (key === 'receipt')     setPreviewType('receipt');
    else if (key === 'credit_note')  setPreviewType('credit_note');
    else if (key === 'quotation')    setPreviewType('quotation');
    else                             setPreviewType('invoice');
  };
  const [form, setForm] = useState({
    firm_name: '', firm_address: '', firm_address2: '', firm_city: '', firm_pincode: '', additional_address: '',
    firm_gstin: '', firm_pan: '', firm_mobile: '', firm_phone: '',
    firm_email: '', firm_state: 'Uttar Pradesh', firm_website: '',
    firm_type: '', proprietor_name: '',
    signature_name: '', signature_designation: '',
    is_gst_registered: true, logo_url: '', signature_url: '',
    logo_x: 0, logo_y: 0, logo_scale: 1,
    invoice_prefix: 'CX', invoice_heading: 'Tax Invoice',
    invoice_footer: '', invoice_footer_html: '',
    invoice_auto_number: true, invoice_start_number: '001',
    show_ledger_balance: false, show_sac_code: true, show_invoice_qr: true,
    template_style: 'corporate', primary_color: '#0f1f45', invoice_font: 'Plus Jakarta Sans', header_style: 'navy',
    show_scope_of_work: true, show_discount_column: true,
    show_watermark: false, watermark_text: 'PAID', due_days: 15,
    bank_name: '', bank_account: '', bank_ifsc: '', bank_branch: '', upi_id: '',
    terms_and_conditions: '',
    receipt_prefix: 'REC', receipt_heading: 'Receipt',
    receipt_footer: '', receipt_footer_html: '',
    receipt_auto_number: true, receipt_start_number: '001',
    show_receipt_ledger: false, show_receipt_remark: false, show_receipt_settlement: true, show_receipt_services: true,
    quotation_heading: 'Quotation', quotation_prefix: 'QT',
    quotation_footer: '', quotation_footer_html: '', quotation_terms: '',
    quotation_auto_number: true, quotation_start_number: '001',
    show_quotation_upi: false, quotation_valid_days: 30,
    credit_note_heading: 'Credit Note', credit_note_prefix: 'CN',
    credit_note_footer: '', credit_note_footer_html: '',
    credit_note_auto_number: true, credit_note_start_number: '001',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    fetch(`${BASE}/api/invoices/settings/config`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.json()).then(data => {
      // Classic and Bold templates were retired — any firm still holding one of
      // those from before now renders as Corporate (the only style left) until
      // they hit Save, which persists the change.
      if (data?.id) setForm(prev => ({ ...prev, ...data, template_style: 'corporate' }));
      setSettingsLoaded(true); // triggers key change → RichTextEditors remount with correct value
    }).catch(() => setSettingsLoaded(true));
  }, [token]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([k]) => !k.startsWith('_') && k !== 'id' && k !== 'created_at' && k !== 'updated_at')
      );
      const res = await fetch(`${BASE}/api/invoices/settings/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showToast('Settings save failed: ' + (err.message || `Server error ${res.status}`));
        return;
      }
      const savedData = await res.json();
      // Sync form with what DB actually stored (ensures consistency)
      if (savedData?.id) setForm(prev => ({ ...prev, ...savedData }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      window.dispatchEvent(new CustomEvent('settings-updated'));
    } catch (e) {
      console.error(e);
      showToast('Could not connect to server. Please try again.');
    }
    finally { setSaving(false); }
  };

  const handleImageUpload = (field, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, [field]: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const Toggle = ({ value, onChange, label }) => (
    <div className="flex items-center gap-3">
      <button type="button" onClick={() => onChange(!value)}
        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${value ? 'bg-blue-600' : 'bg-gray-200'}`}
        style={!value && darkMode ? { background: '#334155' } : {}}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-5' : ''}`}></span>
      </button>
      <span className="text-sm" style={{ color: T.text2 }}>{label}</span>
    </div>
  );

  // Lets the firm restart a document series (invoice/receipt/quotation/credit note)
  // from a chosen number even after documents already exist under the current prefix —
  // editing the "Starting Number" field alone has no effect once the series has begun.
  const ResetNumbering = ({ type, label, prefix }) => {
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState(form[`${type}_start_number`] || '001');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');

    const submit = async () => {
      const clean = value.replace(/[^0-9]/g, '') || '1';
      if (!await showConfirm(`Reset ${label} numbering? The next ${label.toLowerCase()} issued will be "${prefix || ''}${clean}". This cannot be undone.`)) return;
      setBusy(true);
      try {
        const res = await fetch(`${BASE}/api/invoices/settings/reset-numbering`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ type, start_number: clean })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || `Server error ${res.status}`);
        setForm(f => ({ ...f, [`${type}_start_number`]: clean }));
        setMsg(`Done — next ${label.toLowerCase()} will be ${prefix || ''}${data.next_number}`);
        setOpen(false);
        setTimeout(() => setMsg(''), 5000);
      } catch (e) {
        showToast('Reset failed: ' + e.message);
      } finally { setBusy(false); }
    };

    return (
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px dashed ${T.border}` }}>
        {!open ? (
          <button type="button" onClick={() => { setValue(form[`${type}_start_number`] || '001'); setOpen(true); }}
            style={{ fontSize: 12, fontWeight: 600, color: '#dc2626', background: 'transparent', border: `1px solid ${darkMode ? 'rgba(220,38,38,0.4)' : '#fecaca'}`, borderRadius: 8, padding: '7px 14px', cursor: 'pointer' }}>
            Reset Numbering
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: T.text3 }}>Restart numbering from</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input type="text" value={value} onChange={e => setValue(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="001" className={ic} style={{ ...icS, maxWidth: 120 }} />
              <button type="button" disabled={busy} onClick={submit}
                style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#dc2626', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                {busy ? 'Resetting…' : 'Confirm Reset'}
              </button>
              <button type="button" onClick={() => setOpen(false)}
                style={{ fontSize: 12, fontWeight: 600, color: T.text2, background: 'transparent', border: `1px solid ${T.border2}`, borderRadius: 8, padding: '8px 16px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
            <p style={{ fontSize: 11, color: T.text3 }}>
              Next {label.toLowerCase()} will be issued as <b>{prefix || ''}{value || '001'}</b>. Use this after the sequence has already started — e.g. to correct a mistake or restart for a new financial year.
            </p>
          </div>
        )}
        {msg && <p style={{ fontSize: 11, color: '#16a34a', marginTop: 8, fontWeight: 600 }}>{msg}</p>}
      </div>
    );
  };

  // Renders the logo the same way it prints — clipped to the letterhead slot
  // with the saved pan/zoom applied — so the preview always matches the PDF.
  const PositionedLogo = () => (
    <div style={{ width: LOGO_SLOT_W, height: LOGO_SLOT_H, overflow: 'hidden', position: 'relative' }}>
      <img src={form.logo_url} alt="Logo" draggable={false} style={{
        position: 'absolute', left: '50%', top: '50%',
        maxWidth: LOGO_SLOT_W - 20, maxHeight: LOGO_SLOT_H - 12, width: 'auto', height: 'auto', objectFit: 'contain',
        transform: `translate(-50%,-50%) translate(${form.logo_x || 0}px,${form.logo_y || 0}px) scale(${form.logo_scale || 1})`,
      }} />
    </div>
  );

  // ic — base class for inputs/selects/textareas (structure only, colors via icS inline style)
  const ic = "w-full rounded-lg px-3 py-2 text-sm focus:outline-none transition";
  const icS = { background: T.inputBg, border: `1px solid ${T.inputBorder}`, color: T.text };

  const SECTIONS = [
    { key: 'company',   label: 'Company' },
    { key: 'template',  label: 'Template Style' },
    { key: 'invoice',   label: 'Invoice' },
    { key: 'receipt',   label: 'Receipt' },
    { key: 'quotation', label: 'Quotation' },
    { key: 'credit_note', label: 'Credit Note' },
  ];

  const STYLE_CFG = {
    classic:   { headerBg:'#0f1f45', headerText:'#fff', theadBg:'#0f1f45', theadText:'#fff', totalBg:'#0f1f45', totalText:'#fff' },
    corporate: { headerBg:'#ffffff', headerText:'#0f1f45', theadBg:'#0f1f45', theadText:'#fff', totalBg:'#0f1f45', totalText:'#fff' },
    bold:      { headerBg:'#1c1c2e', headerText:'#fff', theadBg:'#f97316', theadText:'#fff', totalBg:'#f97316', totalText:'#fff' },
  };

  const FONTS = ['Plus Jakarta Sans', 'Arial', 'Georgia', 'Poppins', 'Inter', 'Times New Roman', 'Trebuchet MS', 'Montserrat'];

  const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu & Kashmir','Ladakh','Puducherry','Chandigarh'];

  const previewRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(0.55);
  useEffect(() => {
    const update = () => { if (previewRef.current) setPreviewScale(previewRef.current.offsetWidth / 794); };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const cfg = STYLE_CFG[form.template_style] || STYLE_CFG.corporate;
  const previewColor = form.primary_color || '#0f1f45';
  const isCorporate = form.template_style === 'corporate';
  const isBold = form.template_style === 'bold';
  const PRINT_CSS = `<style>@page{size:A4;margin:12mm}body{margin:0}*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}</style>`;
  
  return (
    <div style={{ display: 'flex', gap: 20, minHeight: '85vh', background: T.bg }}>

      {/* ── LEFT: SETTINGS PANEL ── */}
      <div style={{ display: 'flex', flexDirection: 'column', width: '50%', minWidth: 0 }}>
        {/* Section Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
          {SECTIONS.map(s => (
            <button key={s.key} onClick={() => handleSectionChange(s.key)}
              style={{
                padding: '7px 14px', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s',
                background: activeSection === s.key ? '#0f1f45' : T.cardBg,
                color: activeSection === s.key ? '#fff' : T.text2,
                border: `1px solid ${activeSection === s.key ? '#0f1f45' : T.border}`,
              }}>
              {s.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4, scrollbarWidth: 'none' }}>

      {/* ── COMPANY SETTINGS ── */}
      {activeSection === 'company' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Logo */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight: 700, color: T.text, marginBottom: 16, fontSize: 13 }}>Logo</h4>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
              <div style={{ width: 160, height: 96, borderRadius: 12, border: `2px dashed ${T.border2}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: T.bg3 }}>
                {form.logo_url
                  ? <img src={form.logo_url} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }} />
                  : <span style={{ color: T.text3, fontSize: 11, textAlign: 'center' }}>No logo</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ cursor: 'pointer' }}>
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleImageUpload('logo_url', e)} />
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 8, border: `1px solid ${T.border2}`, fontSize: 13, color: T.text2, background: T.bg2, cursor: 'pointer' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    Upload Logo
                  </span>
                </label>
                {form.logo_url && (
                  <button onClick={() => setForm(f => ({ ...f, logo_url: '' }))}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 8, border: '1px solid #fecaca', fontSize: 13, color: '#ef4444', background: darkMode ? 'rgba(239,68,68,0.08)' : '#fff', cursor: 'pointer' }}>
                    ✕ Remove
                  </button>
                )}
                <p style={{ fontSize: 11, color: T.text3 }}>PNG, JPG — max 2MB<br/>Printed on invoices</p>
              </div>
            </div>
            {form.logo_url && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: T.text2, marginBottom: 10 }}>Position &amp; Size</p>
                <LogoPositionEditor
                  logoUrl={form.logo_url}
                  x={form.logo_x || 0} y={form.logo_y || 0} scale={form.logo_scale || 1}
                  onChange={({ x, y, scale }) => setForm(f => ({ ...f, logo_x: x, logo_y: y, logo_scale: scale }))}
                  darkMode={darkMode} T={T}
                />
              </div>
            )}
          </div>

          {/* Company Details */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight: 700, color: T.text, marginBottom: 16, fontSize: 13 }}>Firm Details</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Company/ Firm Name *</label>
                <input type="text" placeholder="CHANAKYA X" value={form.firm_name || ''} onChange={e => setForm(f => ({ ...f, firm_name: e.target.value }))} className={ic} style={icS} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Firm Type</label>
                <select value={form.firm_type || ''} onChange={e => setForm(f => ({ ...f, firm_type: e.target.value }))} className={ic} style={icS}>
                  <option value="">Select...</option>
                  <option>Proprietorship</option>
                  <option>Partnership</option>
                  <option>LLP</option>
                  <option>Private Limited</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Proprietor / Partner Name</label>
                <input type="text" placeholder="CA Shivam Singh" value={form.proprietor_name || ''} onChange={e => setForm(f => ({ ...f, proprietor_name: e.target.value }))} className={ic} style={icS} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>GSTIN</label>
                <input type="text" placeholder="22AAAAA0000A1Z5" value={form.firm_gstin || ''} onChange={e => setForm(f => ({ ...f, firm_gstin: e.target.value.toUpperCase() }))} className={ic} style={icS} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>PAN</label>
                <input type="text" placeholder="ABCDE1234F" maxLength={10} value={form.firm_pan || ''} onChange={e => setForm(f => ({ ...f, firm_pan: e.target.value.toUpperCase() }))} className={ic} style={icS} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Mobile No.</label>
                <input type="tel" maxLength={10} placeholder="10 digits" value={form.firm_mobile || form.firm_phone || ''} onChange={e => { const v = e.target.value.replace(/\D/g,'').slice(0,10); setForm(f => ({ ...f, firm_mobile: v, firm_phone: v })); }} className={ic} style={icS} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Email ID</label>
                <input type="email" placeholder="info@legalchanakya.in" value={form.firm_email || ''} onChange={e => setForm(f => ({ ...f, firm_email: e.target.value }))} className={ic} style={icS} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Website</label>
                <input type="text" placeholder="www.legalchanakya.in" value={form.firm_website || ''} onChange={e => setForm(f => ({ ...f, firm_website: e.target.value }))} className={ic} style={icS} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>City</label>
                <input type="text" placeholder="e.g. Lucknow" value={form.firm_city || ''} onChange={e => setForm(f => ({ ...f, firm_city: e.target.value }))} className={ic} style={icS} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Pincode</label>
                <input type="text" maxLength={6} placeholder="6 digits" value={form.firm_pincode || ''} onChange={e => { const v = e.target.value.replace(/\D/g,'').slice(0,6); setForm(f => ({ ...f, firm_pincode: v })); }} className={ic} style={icS} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>State *</label>
                <select value={form.firm_state || 'Uttar Pradesh'} onChange={e => setForm(f => ({ ...f, firm_state: e.target.value }))} className={ic} style={icS}>
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Address Line 1 *</label>
                <textarea rows={2} placeholder="Office no., building, street, area — don't type City, State or Pincode here" value={form.firm_address || ''} onChange={e => setForm(f => ({ ...f, firm_address: e.target.value }))} className={ic} style={icS} />
                <p style={{ fontSize: 11, color: T.text3, marginTop: 4 }}>City, State and Pincode are added automatically from the fields above.</p>
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Address Line 2</label>
                <textarea rows={2} placeholder="Area, Landmark (optional)" value={form.firm_address2 || ''} onChange={e => setForm(f => ({ ...f, firm_address2: e.target.value }))} className={ic} style={icS} />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Additional Place of Business</label>
                <textarea rows={2} placeholder="Branch office address (optional)" value={form.additional_address || ''} onChange={e => setForm(f => ({ ...f, additional_address: e.target.value }))} className={ic} style={icS} />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                {addressOverlap(form).length > 0 && (
                  <div style={{ marginBottom: 8, padding: '9px 12px', borderRadius: 9, background: darkMode ? 'rgba(245,158,11,0.12)' : '#fffbeb', border: `1px solid ${darkMode ? 'rgba(245,158,11,0.35)' : '#fcd34d'}`, color: '#b45309', fontSize: 12, fontWeight: 600 }}>
                    Address Line 1/2 already contains your {addressOverlap(form).join(', ')}. It is not repeated on documents — but please remove it from the address lines so the form stays clean.
                  </div>
                )}
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Printed on Invoice, Receipt, Credit Note &amp; Quotation as</label>
                <div style={{ padding: '10px 12px', borderRadius: 9, border: `1px dashed ${T.border2}`, background: T.bg3, color: T.text, fontSize: 13, lineHeight: 1.6, minHeight: 20 }}>
                  {buildFirmAddress(form) || <span style={{ color: T.text3 }}>Fill the address fields to see the printed address</span>}
                </div>
              </div>
            </div>
          </div>

          {/* GST Toggle */}
          <div style={{ borderRadius: 12, padding: 20, border: `2px solid ${form.is_gst_registered ? (darkMode ? 'rgba(16,185,129,0.4)' : '#6ee7b7') : T.border}`, background: form.is_gst_registered ? (darkMode ? 'rgba(16,185,129,0.08)' : '#f0fdf4') : T.bg3 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontWeight: 700, color: T.text, fontSize: 14 }}>Charge GST in Invoice</p>
                <p style={{ fontSize: 13, color: T.text3, marginTop: 4 }}>
                  {form.is_gst_registered ? 'Invoices will be Tax Invoices with CGST/SGST/IGST' : 'Invoices will be plain Invoices without GST'}
                </p>
              </div>
              <button type="button" onClick={() => setForm(f => ({ ...f, is_gst_registered: !f.is_gst_registered }))}
                style={{ position: 'relative', width: 52, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer', flexShrink: 0, background: form.is_gst_registered ? '#10b981' : (darkMode ? '#334155' : '#d1d5db'), transition: 'background 0.2s' }}>
                <span style={{ position: 'absolute', top: 3, left: form.is_gst_registered ? 27 : 3, width: 22, height: 22, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'left 0.2s' }}></span>
              </button>
            </div>
          </div>

          {/* Signature */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight: 700, color: T.text, marginBottom: 4, fontSize: 13 }}>Authorised Signatory</h4>
            <p style={{ fontSize: 11, color: T.text3, marginBottom: 16 }}>Name and image printed at the bottom of every invoice, receipt and quotation</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Signatory Name</label>
                <input type="text" placeholder="CA Shivam Singh" value={form.signature_name || ''} onChange={e => setForm(f => ({ ...f, signature_name: e.target.value }))} className={ic} style={icS} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5 }}>Designation</label>
                <input type="text" placeholder="Proprietor / Partner" value={form.signature_designation || ''} onChange={e => setForm(f => ({ ...f, signature_designation: e.target.value }))} className={ic} style={icS} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
              <div style={{ width: 160, height: 96, borderRadius: 12, border: `2px dashed ${T.border2}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: T.bg3 }}>
                {form.signature_url
                  ? <img src={form.signature_url} alt="Signature" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }} />
                  : <span style={{ color: T.text3, fontSize: 11 }}>No signature</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ cursor: 'pointer' }}>
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleImageUpload('signature_url', e)} />
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 8, border: `1px solid ${T.border2}`, fontSize: 13, color: T.text2, background: T.bg2, cursor: 'pointer' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                    Upload Signature
                  </span>
                </label>
                {form.signature_url && (
                  <button onClick={() => setForm(f => ({ ...f, signature_url: '' }))}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 8, border: '1px solid #fecaca', fontSize: 13, color: '#ef4444', background: darkMode ? 'rgba(239,68,68,0.08)' : '#fff', cursor: 'pointer' }}>
                    ✕ Remove
                  </button>
                )}
                <p style={{ fontSize: 11, color: T.text3 }}>PNG (transparent bg recommended)<br/>Max 2MB · Printed on documents</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── INVOICE SETTINGS ── */}
      {activeSection === 'invoice' && (
        <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

          {/* Heading */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:16, fontSize:13 }}>Invoice Heading</h4>
            <div>
              <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Heading *</label>
              <input type="text" placeholder="e.g. Tax Invoice / Invoice / Bill"
                value={form.invoice_heading}
                onChange={e => setForm(f => ({ ...f, invoice_heading: e.target.value }))}
                className={ic} style={icS} />
              <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>This heading appears on every invoice PDF (e.g. "Tax Invoice", "Invoice", "Bill of Supply")</p>
            </div>
          </div>

          {/* Invoice Number */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <div className="flex items-center justify-between mb-4">
              <h4 style={{ fontWeight:700, color:T.text, fontSize:13 }}>Invoice Number</h4>
              <Toggle
                value={form.invoice_auto_number}
                onChange={v => setForm(f => ({ ...f, invoice_auto_number: v }))}
                label="Auto generate invoice number" />
            </div>
            {form.invoice_auto_number && (
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Prefix</label>
                  <input type="text" placeholder="e.g. LCCA/INV/2026-27/"
                    value={form.invoice_prefix || ''}
                    onChange={e => setForm(f => ({ ...f, invoice_prefix: e.target.value }))}
                    className={ic} style={icS} />
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Include trailing separator (e.g. /). Change yearly to reset count.</p>
                </div>
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Starting Number</label>
                  <input type="text" placeholder="001"
                    value={form.invoice_start_number || '001'}
                    onChange={e => setForm(f => ({ ...f, invoice_start_number: e.target.value.replace(/[^0-9]/g, '') || '001' }))}
                    className={ic} style={icS} />
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Leading zeros set padding: 001 = 3-digit, 0001 = 4-digit</p>
                </div>
                <div className="col-span-2">
                  <div style={{ background:darkMode?"rgba(59,130,246,0.12)":"#eff6ff", borderRadius:10, padding:12 }}>
                    <p style={{ fontSize:11, color:"#3b82f6", fontWeight:600 }}>Preview:</p>
                    <p style={{ fontSize:13, fontWeight:800, color:darkMode?"#93c5fd":"#1e40af", marginTop:4 }}>
                      {form.invoice_prefix || 'INV/'}{form.invoice_start_number || '001'}
                    </p>
                    <p style={{ fontSize:11, color:"#60a5fa", marginTop:4 }}>Format: Prefix + Sequential Number. Update prefix each year to reset count (e.g. LCCA/INV/2027-28/)</p>
                  </div>
                </div>
                <div className="col-span-2">
                  <ResetNumbering type="invoice" label="Invoice" prefix={form.invoice_prefix} />
                </div>
              </div>
            )}
          </div>

          {/* Bank Details */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:16, fontSize:13 }}>Bank Details (shown on invoice)</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Bank Name</label>
                <input type="text" value={form.bank_name || ''} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))} className={ic} style={icS} />
              </div>
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Account Number</label>
                <input type="text" value={form.bank_account || ''} onChange={e => setForm(f => ({ ...f, bank_account: e.target.value }))} className={ic} style={icS} />
              </div>
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>IFSC Code</label>
                <input type="text" value={form.bank_ifsc || ''} onChange={e => setForm(f => ({ ...f, bank_ifsc: e.target.value }))} className={ic} style={icS} />
              </div>
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Branch</label>
                <input type="text" value={form.bank_branch || ''} onChange={e => setForm(f => ({ ...f, bank_branch: e.target.value }))} className={ic} style={icS} />
              </div>
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>UPI ID</label>
                <input type="text" placeholder="yourname@upi" value={form.upi_id || ''} onChange={e => setForm(f => ({ ...f, upi_id: e.target.value }))} className={ic} style={icS} />
              </div>
            </div>
          </div>

          {/* Invoice Footer */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:4, fontSize:13 }}>Invoice Footer</h4>
            <p style={{ fontSize:11, color:T.text3, marginBottom:12 }}>Appears at the bottom of every invoice — supports rich text formatting</p>
            <RichTextEditor
              key={settingsLoaded ? 'inv-loaded' : 'inv-loading'}
              value={form.invoice_footer_html || ''}
              onChange={v => setForm(f => ({ ...f, invoice_footer_html: v }))}
              placeholder="e.g. Payment due within 30 days. Subject to local jurisdiction..."
              minHeight="140px" darkMode={darkMode}
            />
          </div>

          {/* Display Options */}
          <div style={{ background:T.cardBg, border:`1px solid ${T.cardBorder}`, borderRadius:12, padding:20, display:"flex", flexDirection:"column", gap:12 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:8, fontSize:13 }}>Display Options</h4>
            <Toggle value={form.show_ledger_balance} onChange={v => setForm(f => ({ ...f, show_ledger_balance: v }))} label="Show ledger balance in invoice" />
            <Toggle value={form.show_sac_code} onChange={v => setForm(f => ({ ...f, show_sac_code: v }))} label="Show SAC code in invoice" />
            <Toggle value={form.show_invoice_qr} onChange={v => setForm(f => ({ ...f, show_invoice_qr: v }))} label="Show UPI QR code on invoice PDF" />
          </div>

          {/* Invoice Content Options */}
          <div style={{ background:T.cardBg, border:`1px solid ${T.cardBorder}`, borderRadius:12, padding:20, display:"flex", flexDirection:"column", gap:12 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:8, fontSize:13 }}>Invoice Content Options</h4>
            <Toggle value={form.show_scope_of_work} onChange={v => setForm(f => ({ ...f, show_scope_of_work: v }))} label="Show Scope of Work / Deliverables" />
            <Toggle value={form.show_discount_column} onChange={v => setForm(f => ({ ...f, show_discount_column: v }))} label="Show Discount column in items table" />
            <Toggle value={form.show_watermark} onChange={v => setForm(f => ({ ...f, show_watermark: v }))} label="Enable Watermark on invoice" />
            {form.show_watermark && (
              <div className="ml-8 mt-2">
                <label className="block text-xs font-medium text-gray-600 mb-2">Watermark Text</label>
                <div className="flex gap-2 flex-wrap">
                  {['PAID','DRAFT','CANCELLED','DUPLICATE'].map(w => (
                    <button key={w} type="button" onClick={() => setForm(f => ({ ...f, watermark_text: w }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${form.watermark_text === w ? 'text-white border-transparent' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                      style={form.watermark_text === w ? { background: '#0f1f45' } : {}}>
                      {w}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Payment Terms */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:12, fontSize:13 }}>Payment Terms</h4>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <label style={{ fontSize:13, color:T.text2, flexShrink:0 }}>Invoice due in</label>
              <input type="number" min="0" value={form.due_days || 15}
                onChange={e => setForm(f => ({ ...f, due_days: parseInt(e.target.value) || 0 }))}
                style={{ width:72, textAlign:'center', background:T.inputBg, border:`1px solid ${T.inputBorder}`, color:T.text, borderRadius:8, padding:'8px 12px', fontSize:13, outline:'none' }} />
              <label style={{ fontSize:13, color:T.text2 }}>days</label>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:4, fontSize:13 }}>Terms &amp; Conditions</h4>
            <p style={{ fontSize:11, color:T.text3, marginBottom:12 }}>Printed on invoice PDF when no footer HTML is set. One line per point.</p>
            <textarea
              rows={5}
              placeholder={"1. Payment due within 15 days of invoice date.\n2. Late payment attracts 2% interest per month.\n3. Subject to Lucknow jurisdiction only.\n4. Goods once sold will not be taken back."}
              value={form.terms_and_conditions || ''}
              onChange={e => setForm(f => ({ ...f, terms_and_conditions: e.target.value }))}
              className={ic}
              style={{ ...icS, resize:'vertical', lineHeight:1.8 }}
            />
          </div>
        </div>
      )}

      {/* ── RECEIPT SETTINGS ── */}
      {activeSection === 'receipt' && (
        <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

          {/* Heading */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:16, fontSize:13 }}>Receipt Heading</h4>
            <div>
              <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Heading *</label>
              <input type="text" placeholder="e.g. Receipt / Payment Receipt"
                value={form.receipt_heading}
                onChange={e => setForm(f => ({ ...f, receipt_heading: e.target.value }))}
                className={ic} style={icS} />
              <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>This heading appears on every receipt PDF</p>
            </div>
          </div>

          {/* Receipt Number */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <div className="flex items-center justify-between mb-4">
              <h4 style={{ fontWeight:700, color:T.text, fontSize:13 }}>Receipt Number</h4>
              <Toggle
                value={form.receipt_auto_number}
                onChange={v => setForm(f => ({ ...f, receipt_auto_number: v }))}
                label="Auto generate receipt number" />
            </div>
            {form.receipt_auto_number && (
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Prefix</label>
                  <input type="text" placeholder="e.g. LCCA/REC/2026-27/"
                    value={form.receipt_prefix || ''}
                    onChange={e => setForm(f => ({ ...f, receipt_prefix: e.target.value }))}
                    className={ic} style={icS} />
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Include trailing separator. Change yearly to reset count.</p>                </div>
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Number starts from</label>
                  <input type="text" placeholder="001"
                    value={form.receipt_start_number || '001'}
                    onChange={e => setForm(f => ({ ...f, receipt_start_number: e.target.value.replace(/[^0-9]/g, '') || '001' }))}                    className={ic} style={icS} />
                </div>
                <div className="col-span-2">
                  <div style={{ background:darkMode?"rgba(16,185,129,0.12)":"#f0fdf4", borderRadius:10, padding:12 }}>
                    <p style={{ fontSize:11, color:"#16a34a", fontWeight:600 }}>Preview:</p>
                    <p style={{ fontSize:13, fontWeight:800, color:darkMode?"#6ee7b7":"#166534", marginTop:4 }}>
                      {form.receipt_prefix || 'REC/'}{form.receipt_start_number || '001'}
                    </p>
                    <p style={{ fontSize:11, color:"#34d399", marginTop:4 }}>Resets every April 1st</p>
                  </div>
                </div>
                <div className="col-span-2">
                  <ResetNumbering type="receipt" label="Receipt" prefix={form.receipt_prefix} />
                </div>
              </div>
            )}
          </div>

          {/* Receipt Footer */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:4, fontSize:13 }}>Receipt Footer</h4>
            <p style={{ fontSize:11, color:T.text3, marginBottom:12 }}>Appears at the bottom of every receipt PDF</p>
            <RichTextEditor
              key={settingsLoaded ? 'rec-loaded' : 'rec-loading'}
              value={form.receipt_footer_html || ''}
              onChange={v => setForm(f => ({ ...f, receipt_footer_html: v }))}
              placeholder="Footer text for receipts..."
              minHeight="120px" darkMode={darkMode}
            />
          </div>

          {/* Display Options */}
          <div style={{ background:T.cardBg, border:`1px solid ${T.cardBorder}`, borderRadius:12, padding:20, display:"flex", flexDirection:"column", gap:12 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:8, fontSize:13 }}>Display Options</h4>
            <Toggle value={form.show_receipt_ledger} onChange={v => setForm(f => ({ ...f, show_receipt_ledger: v }))} label="Show Ledger Balance in Receipt" />
            <Toggle value={form.show_receipt_remark} onChange={v => setForm(f => ({ ...f, show_receipt_remark: v }))} label="Show Remark / Notes in Receipt" />
            <Toggle value={form.show_receipt_settlement} onChange={v => setForm(f => ({ ...f, show_receipt_settlement: v }))} label="Show Invoice Settlement Table in Receipt" />
            <Toggle value={form.show_receipt_services !== false} onChange={v => setForm(f => ({ ...f, show_receipt_services: v }))} label="Show Service Name against each Invoice in Receipt" />
          </div>
        </div>
      )}

      {/* ── QUOTATION SETTINGS ── */}
      {activeSection === 'quotation' && (
        <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

          {/* Heading */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:16, fontSize:13 }}>Quotation Heading</h4>
            <div>
              <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Heading *</label>
              <input type="text" placeholder="e.g. Quotation / Proposal / Estimate"
                value={form.quotation_heading}
                onChange={e => setForm(f => ({ ...f, quotation_heading: e.target.value }))}
                className={ic} style={icS} />
              <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>This heading appears on every quotation PDF</p>
            </div>
          </div>

          {/* Quotation Number */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <div className="flex items-center justify-between mb-4">
              <h4 style={{ fontWeight:700, color:T.text, fontSize:13 }}>Quotation Number</h4>
              <Toggle
                value={form.quotation_auto_number}
                onChange={v => setForm(f => ({ ...f, quotation_auto_number: v }))}
                label="Auto generate quotation number" />
            </div>
            {form.quotation_auto_number && (
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Prefix</label>
                  <input type="text" placeholder="e.g. LCCA/QT/2026-27/"
                    value={form.quotation_prefix || ''}
                    onChange={e => setForm(f => ({ ...f, quotation_prefix: e.target.value }))}
                    className={ic} style={icS} />
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Include trailing separator. Change yearly to reset count.</p>
                </div>
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Number starts from</label>
                  <input type="text" placeholder="001"
                    value={form.quotation_start_number || '001'}
                    onChange={e => setForm(f => ({ ...f, quotation_start_number: e.target.value.replace(/[^0-9]/g, '') || '001' }))}
                    className={ic} style={icS} />
                </div>
                <div className="col-span-2">
                  <div style={{ background:darkMode?"rgba(139,92,246,0.12)":"#f5f3ff", borderRadius:10, padding:12 }}>
                    <p style={{ fontSize:11, color:"#7c3aed", fontWeight:600 }}>Preview:</p>
                    <p style={{ fontSize:13, fontWeight:800, color:darkMode?"#c4b5fd":"#4c1d95", marginTop:4 }}>
                      {form.quotation_prefix || 'QT/'}{form.quotation_start_number || '001'}
                    </p>
                    <p style={{ fontSize:11, color:"#a78bfa", marginTop:4 }}>Update prefix yearly to reset count (e.g. LCCA/QT/2027-28/)</p>                  </div>
                </div>
                <div className="col-span-2">
                  <ResetNumbering type="quotation" label="Quotation" prefix={form.quotation_prefix ? `${form.quotation_prefix}/` : ''} />
                </div>
              </div>
            )}
          </div>

          {/* Quotation Validity */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:12, fontSize:13 }}>Quotation Validity</h4>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <label style={{ fontSize:13, color:T.text2, flexShrink:0 }}>Quotation valid for</label>
              <input type="number" min="1" value={form.quotation_valid_days || 30}
                onChange={e => setForm(f => ({ ...f, quotation_valid_days: parseInt(e.target.value) || 0 }))}
                style={{ width:72, textAlign:'center', background:T.inputBg, border:`1px solid ${T.inputBorder}`, color:T.text, borderRadius:8, padding:'8px 12px', fontSize:13, outline:'none' }} />
              <label style={{ fontSize:13, color:T.text2 }}>days</label>
            </div>
            <p style={{ fontSize:11, color:T.text3, marginTop:8 }}>Used as the default "Valid Till" date on quotation PDFs when a quotation doesn't set its own.</p>
          </div>

          {/* Quotation Footer */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:4, fontSize:13 }}>Quotation Footer</h4>
            <p style={{ fontSize:11, color:T.text3, marginBottom:12 }}>Appears at the bottom of every quotation PDF</p>
            <RichTextEditor
              key={settingsLoaded ? 'qt-loaded' : 'qt-loading'}
              value={form.quotation_footer_html || ''}
              onChange={v => setForm(f => ({ ...f, quotation_footer_html: v }))}
              placeholder="e.g. This quotation is valid for 30 days..."
              minHeight="120px" darkMode={darkMode}
            />
          </div>

          {/* Quotation Terms & Conditions */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:4, fontSize:13 }}>Terms &amp; Conditions</h4>
            <p style={{ fontSize:11, color:T.text3, marginBottom:12 }}>Printed on quotation PDF. One line per point.</p>
            <textarea
              rows={5}
              placeholder={"1. This quotation is valid for 30 days from date of issue.\n2. 50% advance required to commence work.\n3. GST will be charged as applicable.\n4. Subject to Lucknow jurisdiction only."}
              value={form.quotation_terms || ''}
              onChange={e => setForm(f => ({ ...f, quotation_terms: e.target.value }))}
              className={ic}
              style={{ ...icS, resize:'vertical', lineHeight:1.8 }}
            />
          </div>

          {/* Display Options */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:12, fontSize:13 }}>Display Options</h4>
            <Toggle
              value={form.show_quotation_upi}
              onChange={v => setForm(f => ({ ...f, show_quotation_upi: v }))}
              label="Show UPI QR Code in quotation" />
          </div>
        </div>
      )}

      {/* ── CREDIT NOTE SETTINGS ── */}
      {activeSection === 'credit_note' && (
        <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

          {/* Heading */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:16, fontSize:13 }}>Credit Note Heading</h4>
            <div>
              <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Heading *</label>
              <input type="text" placeholder="e.g. Credit Note"
                value={form.credit_note_heading}
                onChange={e => setForm(f => ({ ...f, credit_note_heading: e.target.value }))}
                className={ic} style={icS} />
              <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>This heading appears on every credit note PDF</p>
            </div>
          </div>

          {/* Credit Note Number */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <div className="flex items-center justify-between mb-4">
              <h4 style={{ fontWeight:700, color:T.text, fontSize:13 }}>Credit Note Number</h4>
              <Toggle
                value={form.credit_note_auto_number}
                onChange={v => setForm(f => ({ ...f, credit_note_auto_number: v }))}
                label="Auto generate credit note number" />
            </div>
            {form.credit_note_auto_number && (
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Prefix</label>
                  <input type="text" placeholder="e.g. LCCA/CN/2026-27/"
                    value={form.credit_note_prefix || ''}
                    onChange={e => setForm(f => ({ ...f, credit_note_prefix: e.target.value }))}
                    className={ic} style={icS} />
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Include trailing separator. Change yearly to reset count.</p>
                </div>
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Number starts from</label>
                  <input type="text" placeholder="001"
                    value={form.credit_note_start_number || '001'}
                    onChange={e => setForm(f => ({ ...f, credit_note_start_number: e.target.value.replace(/[^0-9]/g, '') || '001' }))}
                    className={ic} style={icS} />
                </div>
                <div className="col-span-2">
                  <div style={{ background:darkMode?"rgba(249,115,22,0.12)":"#fff7ed", borderRadius:10, padding:12 }}>
                    <p style={{ fontSize:11, color:"#ea580c", fontWeight:600 }}>Preview:</p>
                    <p style={{ fontSize:13, fontWeight:800, color:darkMode?"#fdba74":"#9a3412", marginTop:4 }}>
                      {form.credit_note_prefix || 'CN/'}{form.credit_note_start_number || '001'}
                    </p>
                    <p style={{ fontSize:11, color:"#fb923c", marginTop:4 }}>Update prefix yearly to reset count (e.g. LCCA/CN/2027-28/)</p>
                  </div>
                </div>
                <div className="col-span-2">
                  <ResetNumbering type="credit_note" label="Credit Note" prefix={form.credit_note_prefix} />
                </div>
              </div>
            )}
          </div>

          {/* Credit Note Footer */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:4, fontSize:13 }}>Credit Note Footer</h4>
            <p style={{ fontSize:11, color:T.text3, marginBottom:12 }}>Appears at the bottom of every credit note PDF</p>
            <RichTextEditor
              key={settingsLoaded ? 'cn-loaded' : 'cn-loading'}
              value={form.credit_note_footer_html || ''}
              onChange={v => setForm(f => ({ ...f, credit_note_footer_html: v }))}
              placeholder="Footer text for credit notes..."
              minHeight="120px" darkMode={darkMode}
            />
          </div>
        </div>
      )}

      {/* TEMPLATE STYLE section — inserted here, inside left scroll area */}
      {activeSection === 'template' && (
        <div className="space-y-5">

          {/* Style cards */}
          <div style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20 }}>
            <h4 style={{ fontWeight:700, color:T.text, marginBottom:16, fontSize:13 }}>Invoice Style</h4>
            <div style={{ display:'grid', gridTemplateColumns:'minmax(140px,200px)', gap:12, marginBottom:20 }}>
              {[
                { key:'corporate', label:'Corporate',  desc:'White + Navy',   headerBg:'#fff',    theadBg:'#0f1f45' },
              ].map(s => (
                <div key={s.key}
                  style={{ position:'relative', border:'2px solid #3b82f6', borderRadius:12, overflow:'hidden', background:T.bg2, boxShadow:'0 0 0 3px rgba(59,130,246,0.15)' }}>
                  <div style={{ background:s.headerBg, padding:'8px 10px', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:`2px solid ${previewColor}` }}>
                    <div style={{ width:16, height:16, background:`${previewColor}22`, borderRadius:3 }}></div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ width:32, height:4, background:previewColor, borderRadius:2, marginBottom:2 }}></div>
                      <div style={{ width:22, height:3, background:'#ccc', borderRadius:2 }}></div>
                    </div>
                  </div>
                  <div style={{ height:2, background:s.theadBg }}></div>
                  <div style={{ padding:'6px 8px', background:T.bg2 }}>
                    <div style={{ height:5, background:s.theadBg, borderRadius:2, marginBottom:3 }}></div>
                    {[1,2].map(i => <div key={i} style={{ height:3, background:T.bg3, borderRadius:2, marginBottom:2 }}></div>)}
                  </div>
                  <div style={{ position:'absolute', top:6, right:6, width:16, height:16, background:'#3b82f6', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <div style={{ textAlign:'center', padding:'5px 4px 8px', background:T.bg2 }}>
                    <p style={{ fontSize:11, fontWeight:700, color:T.text, margin:0 }}>{s.label}</p>
                    <p style={{ fontSize:10, color:T.text3, margin:'2px 0 0' }}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Color + Font */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Primary Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.primary_color || '#0f1f45'} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))}
                    className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5 flex-shrink-0" />
                  <input type="text" value={form.primary_color || '#0f1f45'} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none font-mono" maxLength={7} />
                </div>
              </div>
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Font</label>
                <select value={form.invoice_font || 'Plus Jakarta Sans'} onChange={e => setForm(f => ({ ...f, invoice_font: e.target.value }))} className={ic} style={icS}>
                  {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>

            {/* Header Style — table header / BILL TO badge / total band color */}
            <div style={{ marginTop:16 }}>
              <label style={{ display:"block", fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Header Style</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { v:'navy', l:'Fixed Navy' },
                  { v:'primary', l:'Primary Color' },
                  { v:'gradient', l:'Primary Gradient' },
                ].map(opt => (
                  <button key={opt.v} type="button" onClick={() => setForm(f => ({ ...f, header_style: opt.v }))}
                    style={{
                      padding:'9px 6px', borderRadius:9, fontSize:12, fontWeight:600, cursor:'pointer',
                      border: (form.header_style||'navy')===opt.v ? 'none' : `1px solid ${T.border2}`,
                      background: (form.header_style||'navy')===opt.v ? '#0f1f45' : 'transparent',
                      color: (form.header_style||'navy')===opt.v ? '#fff' : T.text2,
                    }}>
                    {opt.l}
                  </button>
                ))}
              </div>
              <p style={{ fontSize:11, color:T.text3, marginTop:6 }}>Controls the table header, "Bill To" badge, and total band on the item table — text color auto-adjusts for contrast.</p>
            </div>
          </div>
        </div>
      )}

        </div>{/* end left scroll area */}

        {/* ── SAVE BAR (bottom of left panel) ── */}
        <div style={{ marginTop:16, background:T.cardBg, border:`1px solid ${T.border}`, borderRadius:12, padding:"10px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
          <p style={{ fontSize:11, color:T.text3 }}>
            Section: <span style={{ fontWeight:600, color:T.text2 }}>{activeSection.replace(/_/g,' ')}</span>
          </p>
          <div className="flex items-center gap-3">
            {saved && <span className="text-sm text-green-600 font-medium flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>Saved!
            </span>}
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-60"
              style={{ background: '#0f1f45' }}>
              {saving ? <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg>Saving...</> : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>{/* end left panel */}

      {/* ── RIGHT 50%: LIVE PREVIEW ── */}
      <div style={{ display:'flex', flexDirection:'column', flexShrink:0, width:'50%' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexShrink:0, height:36 }}>
          {/* Preview type switcher — matches tab row height on the left */}
          <div style={{ display:'flex', alignItems:'center', gap:4, background:T.bg3, borderRadius:8, padding:2 }}>
            {[
              { key:'invoice',     label:'Invoice' },
              { key:'receipt',     label:'Receipt' },
              { key:'credit_note', label:'Credit Note' },
              { key:'quotation',   label:'Quotation' },
            ].map(t => (
              <button key={t.key} onClick={() => setPreviewType(t.key)}
                style={{ padding:'5px 12px', borderRadius:6, border:'none', fontSize:12, fontWeight:600, cursor:'pointer', background:previewType===t.key?T.cardBg:'transparent', color:previewType===t.key?T.text:T.text3, boxShadow:previewType===t.key?(darkMode?'0 1px 4px rgba(0,0,0,0.4)':'0 1px 4px rgba(0,0,0,0.1)'):'none', transition:'all 0.15s' }}>
                {t.label}
              </button>
            ))}
          </div>
          {/* Scale + Print */}
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:11, color:T.text3, background:T.cardBg, border:`1px solid ${T.border}`, borderRadius:6, padding:'2px 8px' }}>{Math.round(previewScale * 100)}% scale</span>
            <button onClick={() => {
              const el = document.getElementById('billing-inv-preview');
              if (!el) return;
              const w = window.open('', '_blank');
              const font = form.invoice_font || 'Plus Jakarta Sans';
              const webFonts = ['Plus Jakarta Sans','Poppins','Inter','Montserrat','Roboto','Lato','Nunito','Open Sans'];
              const fontLink = webFonts.includes(font)
                ? `<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=${font.replace(/ /g,'+')}:wght@400;500;600;700&display=swap">`
                : '';
              w.document.write(`<html><head>${fontLink}${PRINT_CSS}<style>body{font-family:'${font}',sans-serif}*{font-family:'${font}',sans-serif}</style></head><body>${el.innerHTML}</body></html>`);
              w.document.close();
              if (fontLink) {
                w.onload = () => setTimeout(() => w.print(), 800);
              } else {
                w.print();
              }
            }}
            style={{ padding:"6px 12px", borderRadius:8, border:`1px solid ${T.border}`, color:T.text2, fontSize:11, background:T.cardBg, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
              Print
            </button>
          </div>
        </div>
        <div ref={previewRef} style={{ flex:1, background:darkMode?"#1e293b":"#e2e8f0", borderRadius:16, overflow:"auto", padding:12, scrollbarWidth:"thin" }}>
          <div style={{ width:'794px', transformOrigin:'top left', transform:`scale(${previewScale})`, height:`${1123 * previewScale}px`, overflow:'hidden', boxShadow:'0 4px 24px rgba(0,0,0,0.12)', borderRadius:'4px' }}>
            <div id="billing-inv-preview" style={{ background:'#fff', fontFamily: form.invoice_font || 'Plus Jakarta Sans', color:'#1a1a1a', fontSize:'11px', width:'794px', position:'relative' }}>

              {previewType === 'invoice' && (
                <InvoiceLetterhead
                  settings={form}
                  invoice={{
                    invoice_number: form.invoice_prefix ? `${form.invoice_prefix}${String(form.invoice_start_number||1).padStart(3,'0')}` : '—',
                    invoice_date: '2026-05-24', due_date: '2026-06-07',
                    client_name: 'Client Name', client_address: `Client Address, ${form.firm_city || 'City'}`,
                    gst_type: form.is_gst_registered ? 'cgst_sgst' : 'none', total_gst: form.is_gst_registered ? 1 : 0,
                    items: [
                      { particular: 'Annual Filing', description: form.show_scope_of_work ? 'Scope of Work\n1. Verification and documentation review\n2. Filing with relevant government portal\nDeliverables\n• Filed acknowledgment copy • Confirmation document' : '', rate: 60000, amount: 60000, net_amount: 60000, discount_amount: 0 },
                      { particular: 'Trademark Hearing — Wordmark', description: form.show_scope_of_work ? 'Scope of Work\n1. Verification and documentation review\n2. Filing with relevant government portal\nDeliverables\n• Filed acknowledgment copy • Confirmation document' : '', rate: 24000, amount: 24000, net_amount: 21000, discount_amount: 3000 },
                    ],
                    subtotal: 84000, discount_amount: 3000, total_amount: 81000, amount_paid: 0, balance_due: 81000,
                  }}
                  qrDataUrl={form.upi_id ? `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`upi://pay?pa=${form.upi_id}&pn=${form.firm_name||''}&cu=INR`)}` : ''}
                />
              )}
              {previewType === 'receipt' && (
                <ReceiptLetterhead
                  settings={form}
                  receipt={{
                    receipt_number: form.receipt_prefix ? `${form.receipt_prefix}/${new Date().getFullYear().toString().slice(-2)}-${(new Date().getFullYear()+1).toString().slice(-2)}/001` : '—',
                    payment_date: new Date().toISOString().split('T')[0], mode: 'bank_transfer', reference_number: 'HDFC0N2839417',
                    client_name: 'Client Name', client_address: `Client Address, ${form.firm_city || 'City'}`,
                    settlements: form.show_receipt_settlement !== false
                      ? [{ invoice_number: form.invoice_prefix ? `${form.invoice_prefix}/001` : 'INV/001', invoice_amount: 13500, amount: 6000, service_names: 'Annual ROC Filing, GST Return (Mar 2026)' }]
                      : [],
                    received_amount: 6000, balance_after: 7500,
                    notes: 'Payment received — Sample Service',
                  }}
                  qrDataUrl={form.upi_id ? `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`upi://pay?pa=${form.upi_id}&pn=${form.firm_name||''}&cu=INR`)}` : ''}
                />
              )}

              {previewType === 'credit_note' && (
                <CreditNoteLetterhead
                  settings={form}
                  creditNote={{
                    credit_note_number: form.credit_note_prefix ? `${form.credit_note_prefix}/26-27/001` : '—',
                    credit_date: new Date().toISOString().split('T')[0],
                    client_name: 'Client Name', client_address: `Client Address, ${form.firm_city || 'City'}`,
                    original_invoice_number: form.invoice_prefix ? `${form.invoice_prefix}/001` : 'INV/001',
                    reason: 'Sample credit reason',
                    items: [{ particular: 'Credit against service — Sample Service', amount: 5000 }],
                    total_amount: 5000,
                  }}
                />
              )}

              {previewType === 'quotation' && (
                <QuotationLetterhead
                  settings={form}
                  quote={{
                    quote_number: form.quotation_prefix ? `${form.quotation_prefix}${String(form.quotation_start_number||1).padStart(3,'0')}` : '—',
                    created_at: new Date().toISOString().split('T')[0],
                    valid_till: new Date(Date.now()+30*864e5).toISOString().split('T')[0],
                    client_name: 'Client Name', source: 'direct',
                    items: [
                      { description: 'Sample Service Name', amount: 60000 },
                      { description: 'Sample Service Name 2', amount: 24000 },
                    ],
                    total_amount: 84000,
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
// ─── Logo Position Editor — drag-to-pan + zoom slider, clipped to a fixed
// "letterhead slot" (dashed border = the actual print/PDF crop area). No new
// dependency: plain mouse-event dragging + a range input for zoom, which
// covers "move and resize within the letterhead" without the complexity of
// true corner resize-handles. ─────────────────────────────────────────────
const LOGO_SLOT_W = 200, LOGO_SLOT_H = 64;
function LogoPositionEditor({ logoUrl, x, y, scale, onChange, darkMode, T }) {
  const dragRef = useRef(null);
  const handleMouseDown = (e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: x, origY: y };
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      onChange({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy, scale });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div>
      <div
        onMouseDown={logoUrl ? handleMouseDown : undefined}
        style={{
          width: LOGO_SLOT_W, height: LOGO_SLOT_H, borderRadius: 6,
          border: `2px dashed ${darkMode ? '#475569' : '#93c5fd'}`,
          background: darkMode ? '#0f172a' : '#f8faff',
          overflow: 'hidden', position: 'relative',
          cursor: logoUrl ? 'grab' : 'default',
        }}>
        {logoUrl ? (
          <img src={logoUrl} alt="Logo" draggable={false}
            style={{
              position: 'absolute', left: '50%', top: '50%',
              maxWidth: LOGO_SLOT_W - 20, maxHeight: LOGO_SLOT_H - 12, width: 'auto', height: 'auto', objectFit: 'contain',
              transform: `translate(-50%,-50%) translate(${x}px,${y}px) scale(${scale})`,
              userSelect: 'none', pointerEvents: 'none',
            }} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: T.text3, fontSize: 11 }}>Upload a logo first</div>
        )}
      </div>
      {logoUrl && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, maxWidth: LOGO_SLOT_W }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{ flexShrink: 0 }}><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input type="range" min="0.3" max="3" step="0.05" value={scale}
            onChange={e => onChange({ x, y, scale: parseFloat(e.target.value) })}
            style={{ flex: 1 }} />
          <button type="button" onClick={() => onChange({ x: 0, y: 0, scale: 1 })}
            style={{ fontSize: 11, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}>
            Reset
          </button>
        </div>
      )}
      <p style={{ fontSize: 11, color: T.text3, marginTop: 6 }}>Drag the logo to reposition · use the slider to resize · dashed box = the letterhead area on your PDFs</p>
    </div>
  );
}

// ─── Rich Text Editor (no external deps — uses contentEditable + execCommand) ───
// NOTE: Always use a unique `key` prop from parent when value loads async,
// e.g. key={`rte-${settingsLoaded}`}. That forces a fresh mount with the correct
// initial value instead of fighting the race between mount and async data load.
function RichTextEditor({ value, onChange, placeholder, minHeight, darkMode = false }) {
  const editorRef = useRef(null);
  const rteCard = darkMode ? '#1e293b' : '#fff';
  const rteBorder = darkMode ? '#334155' : '#e5e7eb';
  const rteToolBg = darkMode ? '#0f172a' : '#f9fafb';
  const rteText = darkMode ? '#f1f5f9' : '#374151';
  const rteHover = darkMode ? '#334155' : '#e5e7eb';

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || '';
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exec = (cmd, val) => {
    editorRef.current && editorRef.current.focus();
    document.execCommand(cmd, false, val || null);
  };

  const TOOLS = [
    { cmd: 'bold', label: <strong>B</strong>, title: 'Bold' },
    { cmd: 'italic', label: <em>I</em>, title: 'Italic' },
    { cmd: 'underline', label: <span style={{ textDecoration: 'underline' }}>U</span>, title: 'Underline' },
    { cmd: 'insertUnorderedList', label: '• List', title: 'Bullet List' },
    { cmd: 'insertOrderedList', label: '1. List', title: 'Numbered List' },
  ];

  return (
    <div style={{ border: `1px solid ${rteBorder}`, borderRadius: 8, overflow: 'hidden', background: rteCard }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, background: rteToolBg, padding: '6px 8px', borderBottom: `1px solid ${rteBorder}`, flexWrap: 'wrap' }}>
        {TOOLS.map(t => (
          <button key={t.cmd} type="button" title={t.title}
            onMouseDown={e => { e.preventDefault(); exec(t.cmd); }}
            style={{ padding: '4px 8px', borderRadius: 4, fontSize: 13, border: 'none', background: 'none', color: rteText, cursor: 'pointer', minWidth: 32, textAlign: 'center' }}
            onMouseEnter={e => e.currentTarget.style.background = rteHover}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            {t.label}
          </button>
        ))}
        <div style={{ width: 1, height: 20, background: rteBorder, margin: '0 4px' }} />
        <button type="button" title="Insert Link"
          onMouseDown={e => {
            e.preventDefault();
            const url = window.prompt('Enter URL (e.g. https://example.com):');
            if (url) exec('createLink', url);
          }}
          style={{ padding: '4px 8px', borderRadius: 4, fontSize: 13, border: 'none', background: 'none', color: rteText, cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.background = rteHover}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}>
          🔗
        </button>
        <button type="button" title="Remove Link"
          onMouseDown={e => { e.preventDefault(); exec('unlink'); }}
          style={{ padding: '4px 8px', borderRadius: 4, fontSize: 11, border: 'none', background: 'none', color: rteText, cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.background = rteHover}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}>
          ✕link
        </button>
        <div style={{ width: 1, height: 20, background: rteBorder, margin: '0 4px' }} />
        <button type="button" title="Clear Formatting"
          onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }}
          style={{ padding: '4px 8px', borderRadius: 4, fontSize: 11, border: 'none', background: 'none', color: rteText, cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.background = rteHover}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}>
          Tx
        </button>
      </div>
      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange && onChange(editorRef.current.innerHTML)}
        style={{ padding: '8px 12px', fontSize: 13, outline: 'none', color: rteText, minHeight: minHeight || '120px', lineHeight: '1.7', background: rteCard }}
        data-placeholder={placeholder}
      />
      <style>{`[data-placeholder]:empty::before{content:attr(data-placeholder);color:#9ca3af;pointer-events:none;font-style:italic;font-size:13px}`}</style>
    </div>
  );
}

export default BillingSettings;